// screens/staff/UrgentBanner.tsx
//
// W6 v1.1 Session 1 — in-app urgent-event banner per spec §5.6.
//
// Mounted at the top of InstructorHomeScreen (`/staff/home`). Reads
// alert_state via GET /api/staff/alerts/recent?kinds=urgent_event&hours=24.
//
// - aria-live="polite" so a fresh urgent alert is announced if the user is
//   on the dashboard when it lands.
// - Italian "Lei" copy.
// - Dismiss button → POST /api/staff/alerts/:id/dismiss writes last_seen_at.
//   Once dismissed, the row is filtered locally; the next mount would still
//   show it if last_seen_at is older than the row's last_sent_at, but for
//   v1.1 we treat any last_seen_at as "seen" and hide it on subsequent loads.
// - Hidden when there are no UNREAD urgent alerts (last_seen_at IS NULL).
//
// Visible only to titolare + instructor_*; the side-nav and route guard
// already exclude segreteria from /staff/home, so this component is never
// rendered for the segreteria role.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useTokens } from '../../state/theme';
import { staffApi, type AlertRow } from '../../api/staff';

interface UrgentPayload {
  student_first_name?: string;
  argomento_name?:     string | null;
  trigger?:            string;
}

export default function UrgentBanner(): JSX.Element | null {
  const tokens = useTokens();
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.alertsRecent({ kinds: ['urgent_event'], hours: 24 });
      // Filter unread only — last_seen_at IS NULL.
      setAlerts(r.alerts.filter(a => a.last_seen_at === null));
    } catch {
      // Silent fail on the banner — the dashboard cards still render.
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    reload().then(() => { if (!alive) return; });
    return () => { alive = false; };
  }, [reload]);

  const handleDismiss = useCallback(async (id: string) => {
    setDismissing(prev => new Set(prev).add(id));
    try {
      await staffApi.dismissAlert(id);
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch {
      // Re-show on failure
    } finally {
      setDismissing(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  // Hidden when loading initial OR no unread urgent alerts. The dashboard
  // doesn't want a "loading…" placeholder above the 6 cards on every fetch;
  // the banner is best-effort.
  if (loading || alerts.length === 0) return null;

  return (
    <section
      data-testid="urgent-banner"
      role="region"
      aria-live="polite"
      aria-label="Avvisi urgenti"
      style={{
        background:    tokens.wrongSoft,
        border:        `1px solid ${tokens.wrong}`,
        borderRadius:  tokens.radius.lg,
        padding:       '14px 18px',
        marginBottom:  20,
        color:         tokens.text,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span aria-hidden="true" style={{ color: tokens.wrong, fontSize: 18, fontWeight: 700 }}>!</span>
        <h2 style={{ fontFamily: tokens.fonts.serif, fontSize: 15, fontWeight: 700, margin: 0, color: tokens.wrong }}>
          {alerts.length === 1 ? 'Segnalazione urgente' : `${alerts.length} segnalazioni urgenti`}
        </h2>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {alerts.map(a => {
          const p = a.payload as UrgentPayload;
          const isDismissing = dismissing.has(a.id);
          return (
            <li
              key={a.id}
              data-testid={`urgent-banner-row-${a.id}`}
              style={{
                display:        'flex',
                justifyContent: 'space-between',
                alignItems:     'center',
                gap:            12,
                padding:        '6px 0',
                borderTop:      `1px solid ${tokens.line}`,
              }}
            >
              <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                <strong>{p.student_first_name ?? 'Studente'}</strong>{' '}
                ha sbagliato ripetutamente un principio chiave dell'esame
                {p.argomento_name ? <> su <strong>{p.argomento_name}</strong></> : null}.
              </div>
              <button
                onClick={() => handleDismiss(a.id)}
                disabled={isDismissing}
                data-testid={`urgent-banner-dismiss-${a.id}`}
                aria-label={`Segna come letto: avviso ${p.student_first_name ?? ''}`}
                style={{
                  background:    'transparent',
                  color:         tokens.textMuted,
                  border:        `1px solid ${tokens.line}`,
                  borderRadius:  tokens.radius.pill,
                  padding:       '6px 12px',
                  cursor:        isDismissing ? 'wait' : 'pointer',
                  fontSize:      12,
                  fontWeight:    500,
                  whiteSpace:    'nowrap',
                  opacity:       isDismissing ? 0.6 : 1,
                }}
              >
                Segna come letto
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
